/* eslint-disable @typescript-eslint/no-floating-promises */
import type ApplicationInstance from '@ember/application/instance';
import { on } from '@ember/modifier';
import { getOwner } from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type { BufferedChangeset } from 'ember-changeset/types';
import Form from 'ember-wiser/components/form';
import type ApplicationAdapter from 'parog-web/adapters/application';
import FileAttachmentDisplayer from 'parog-web/components/file-attachment-displayer';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import type { ToastMessage } from 'parog-web/components/forc/abstract/journal-event-based-component';
import DraftManager from 'parog-web/components/forc/draft-manager';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import BioExamResultsReceivedJournalEvent from 'parog-web/models/bio-exam-results-received-journal-event';
import type FileAttachment from 'parog-web/models/file-attachment';
import type Patient from 'parog-web/models/patient';
import type AnalyticsService from 'parog-web/services/analytics';
import type NavigationService from 'parog-web/services/navigation';
import type { SweetAlertResult } from 'sweetalert2';
import FileUpload from 'ui-kit/components/changeset/file-upload';
import PageContainer from 'ui-kit/components/page-container';
import emberSwal from 'ui-kit/utils/ember-swal';

interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  model: BioExamResultsReceivedJournalEvent;
  patient: Patient;
}

export default class BioExamResultsNewEditionFormComponent extends CrudJournalEventBaseComponent<
  BioExamResultsReceivedJournalEvent,
  Args
> {
  static override forcDisplayName = 'Résultats biologiques';
  static override fullComponentId = 'bio-exam-results/new-edition-form';
  static override journalEventClass = BioExamResultsReceivedJournalEvent;

  @service declare analytics: AnalyticsService;
  @service declare navigation: NavigationService;
  @service declare router: RouterService;

  override applicationAdapter = (getOwner(this) as ApplicationInstance).lookup(
    'adapter:application'
  ) as ApplicationAdapter;
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  override get changeset() {
    this.draft = new DraftManager(this);
    return this.args.changeset;
  }

  get fileAttachments(): Array<FileAttachment> {
    return (
      (this.changeset.orderedFileAttachments as Array<FileAttachment>) || []
    );
  }

  override get toastMessage(): ToastMessage {
    return {
      message: 'Vos résultats biologiques ont bien été pris en compte.',
      title: 'Biologie'
    };
  }

  back = () => {
    this.navigation.back();
  };

  override async save(): Promise<void> {
    const hasFileAttachment =
      (this.changeset.fileAttachmentsAttributes as boolean) &&
      (this.changeset.fileAttachmentsAttributes as Array<object>).length != 0;
    if (!hasFileAttachment) {
      const result = await this.#fireNoFileAttachmentAlert();
      if (result.isDismissed) {
        return;
      }
    }

    this.changeset.set('normalized', true);
    try {
      if (this.isNewMode) {
        if (hasFileAttachment) {
          const model =
            (await super.save()) as BioExamResultsReceivedJournalEvent;
          await this.#createChatMessage(model);
        }
        this.#createBioTask();
      }

      if (!hasFileAttachment) {
        this.popToast();
      }

      this.shouldForceQuit = true;
      await this.router.transitionTo(
        hasFileAttachment
          ? 'patient-web.protected.chat'
          : 'patient-web.protected.dashboard.index'
      );

      return;
    } catch (error) {
      this.#handleError(error);
    }
  }

  async #createBioTask(): Promise<void> {
    await this.requestManager.request({
      url: `/api/v1/task_planned_journal_event/${this.patient.id}/create_bio_task`,
      method: 'POST',
      body: JSON.stringify({
        data: {
          comments:
            'Date déclarée par le patient : ' +
            this.changeset.get('occurredAt').toLocaleDateString('fr-FR')
        }
      })
    });
  }

  async #createChatMessage(
    bioExamResultsRecievedJE: BioExamResultsReceivedJournalEvent
  ) {
    await this.store
      .createRecord('patient-chat-message-sent-journal-event', {
        occurredAt: new Date(),
        triggeringJournalEvent: bioExamResultsRecievedJE,
        patient: bioExamResultsRecievedJE.patient,
        creator: bioExamResultsRecievedJE.creator,
        body: "Résultats d'examen envoyés"
      })
      .save();
  }

  async #fireNoFileAttachmentAlert(): Promise<SweetAlertResult> {
    return await emberSwal.fire({
      title: 'Voulez-vous nous transmettre un document ?',
      text: "Vous pouvez joindre vos résultats d'analyses maintenant ou plus tard",
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: 'Oui, joindre un document',
      confirmButtonText: 'Non, enregistrer uniquement la date',
      reverseButtons: true
    });
  }

  #handleError(error): void {
    if (error.status == 409) {
      emberSwal.fire({
        title: 'Erreur',
        html: `Des résultats d'analyse biologiques identiques existent déjà à cette date.<br/><br>
          Vous les retrouverez dans <strong>l'historique des bilans de biologie</strong>.`,
        icon: 'error'
      });
    } else {
      emberSwal.fire({
        title: `Échec de l'importation de votre document.`,
        text: 'Veuillez réessayer ou contacter le support Sêmeia si le problème persiste.',
        icon: 'error'
      });
    }

    throw error;
  }

  <template>
    <PageContainer @background='transparent'>
      <:content>
        <p class='mb-8'>
          Partagez simplement vos résultats biologiques avec votre équipe de
          soins grâce à Wise.
        </p>
        <Form @data={{this.changeset}} @onSubmit={{this.onSave}} as |form|>
          <div class='my-4'>
            <form.InputRow
              @name='occurredAt'
              @label='Date des analyses *'
              type='date'
            >&nbsp;</form.InputRow>
          </div>

          <div class='my-4'>
            <label class='text-purple'>
              Résultats biologiques :
            </label>
          </div>
          <div class='my-4'>
            <FileUpload
              @changeset={{this.changeset}}
              @propertyName='fileAttachmentsAttributes'
            />
          </div>

          <div class='my-2'>
            {{#each this.fileAttachments as |fileAttachment|}}
              <FileAttachmentDisplayer
                @shouldDisplayTextWithDownloadIcon={{true}}
                @fileAttachment={{fileAttachment}}
                @alternativeText='pièce jointe'
              />
            {{/each}}
          </div>

          <div class='mt-4 flex justify-end gap-2'>
            <form.CancelButton {{on 'click' this.back}} @icon='ArrowLeft' />
            <form.SubmitButton>Déclarer</form.SubmitButton>
          </div>
        </Form>
      </:content>
    </PageContainer>
  </template>
}
